import { Box, Card } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {ReactElement, useEffect, useState} from 'react';

import { getComparator, useTable } from '@/commons/table';
import { applyFilter } from '@/utils/filter';
import { translates } from '@/utils';
import TableToolbar from './TableToolbar';
import { ListTable } from './list-table';
import { CONSTANT_TABLE } from './list-table/interface';
import { navigateHandleProps } from './list-table/types';
import { ActionsProps } from './types/actions';
import { Paginator } from './types/pagination';

const STATUS_OPTIONS = [
  { value: 'Activo', label: translates.active },
  { value: 'Desactivado', label: translates.disable },
];

interface ListPageProps<T, K = string> {
  isLoading: boolean;
  data: T[];
  dataStruct: CONSTANT_TABLE[];
  navigateHandle?: navigateHandleProps<K>;
  actions?: ({ value, openConfirm, setOpenConfirm, setOpenPopover }: ActionsProps) => JSX.Element;
  actionParamId?: string;
  paginator?: Paginator;
  toolbar?: ReactElement;
}

export default function ListPage<T extends Record<string, unknown>, K>({
  isLoading,
  data = [],
  dataStruct,
  navigateHandle,
  actions,
  actionParamId,
  paginator,
                                                                         toolbar,
}: ListPageProps<T, K>) {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({
    defaultOrderBy: 'createdAt',
    paginator,
  });

  const [tableData, setTableData] = useState<T[]>(data || []);

  const [filterName, setFilterName] = useState('');

  const [filterStatus, setFilterStatus] = useState<string[]>([]);

  const [, setOpenConfirm] = useState(false);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const dataFiltered = applyFilter({
    inputData: tableData,
    comparator: getComparator(order, orderBy),
    filterName,
    filterStatus,
  });

  const dataInPage = dataFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const isFiltered = filterName !== '' || !!filterStatus.length;

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleFilterStatus = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    setPage(0);
    setFilterStatus(typeof value === 'string' ? value.split(',') : value);
  };

  const handleResetFilter = () => {
    setFilterName('');
    setFilterStatus([]);
  };

  return (
    <>
      <Card>
        <TableToolbar
            toolbar={toolbar}
          filterName={filterName}
          filterStatus={filterStatus}
          onFilterName={handleFilterName}
          onFilterStatus={handleFilterStatus}
          statusOptions={STATUS_OPTIONS}
          isFiltered={isFiltered}
          onResetFilter={handleResetFilter}
        />
      </Card>
      <Box sx={{ height: 20 }} />

      <ListTable<T, K>
        paginator={paginator}
        navigateHandle={navigateHandle}
        isLoading={isLoading}
        dense={dense}
        order={order}
        orderBy={orderBy}
        filterName={filterName}
        dataFiltered={dataFiltered}
        selected={selected}
        tableData={tableData}
        onSort={onSort}
        onChangeDense={onChangeDense}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        rowsPerPage={rowsPerPage}
        page={page}
        dataInPage={dataInPage}
        handleOpenConfirm={handleOpenConfirm}
        setSelected={setSelected}
        setPage={setPage}
        onSelectAllRows={onSelectAllRows}
        onSelectRow={onSelectRow}
        dataStruct={dataStruct}
        actions={actions}
        actionParamId={actionParamId}
      />
    </>
  );
}
